import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { interval, Subscription } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { of } from 'rxjs'

import { environment } from 'src/environments/environment'
import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'

/**
 * Service to check for application version updates and force client refresh
 * when a new version is detected.
 */
@Injectable({
    providedIn: 'root'
})
export class VersionCheckService {
    private versionCheckInterval: Subscription | null = null
    private readonly CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes
    private readonly VERSION_FILE_PATH = '/version.json'

    constructor(private http: HttpClient) {}

    /**
     * Start periodic version checking
     */
    startVersionChecking(): void {
        // Check immediately on start
        this.checkVersion()

        // Then check periodically
        this.versionCheckInterval = interval(this.CHECK_INTERVAL).subscribe(() => {
            this.checkVersion()
        })
    }

    /**
     * Stop version checking
     */
    stopVersionChecking(): void {
        if (this.versionCheckInterval) {
            this.versionCheckInterval.unsubscribe()
            this.versionCheckInterval = null
        }
    }

    /**
     * Check if the current version matches the deployed version
     * Fetches version.json file from the server and compares with current version.
     * This is a client-side only check that doesn't rely on backend validation.
     */
    private checkVersion(): void {
        const currentVersion = environment.version

        // If version is 0 or not set, skip version checking (development mode)
        if (!currentVersion || currentVersion === 0) {
            return
        }

        // Fetch version.json file from the server (deployed with each build)
        // This file contains the version of the currently deployed application
        // Add cache-busting query parameter to ensure we get the latest version
        const versionUrl = `${this.VERSION_FILE_PATH}?_t=${Date.now()}`
        this.http
            .get<{ version: number }>(versionUrl)
            .pipe(
                map((response) => {
                    const serverVersion = response?.version

                    if (serverVersion && serverVersion > currentVersion) {
                        // New version available on server, trigger refresh
                        this.triggerVersionUpdate()
                    }
                    return response
                }),
                catchError((error) => {
                    // Silently fail - don't interrupt user experience
                    // If version.json doesn't exist or can't be fetched, that's okay
                    // Chunk error detection will still catch version mismatches
                    console.debug('Version check failed:', error)
                    return of(null)
                })
            )
            .subscribe()
    }

    /**
     * Trigger version update event
     */
    private triggerVersionUpdate(): void {
        document.dispatchEvent(
            new CustomEvent('version-refresh-alert', {
                detail: { data: { versionUpdate: true } }
            })
        )
    }

    /**
     * Check version on chunk load error
     * This is called when a chunk fails to load, indicating a version mismatch
     */
    handleChunkError(): void {
        this.triggerVersionUpdate()
    }
}

